/**
 * Calls over time — Recharts stacked bar: hour/day × inbound-outbound or wrap codes.
 */
import { useState, useEffect, useMemo } from 'react';
import { useAuth } from '@clerk/clerk-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import {
  buildCallHistoryParams,
  colorForStackSeries,
  type WidgetFilterConfig,
} from '../../lib/widgetScope';
import type { WidgetDateRange } from '../../lib/widgetDateRange';

type ChartRow = Record<string, string | number>;

interface WidgetCallHourlyChartProps {
  config?: WidgetFilterConfig;
}

function seriesDisplayName(key: string): string {
  if (key === 'inbound') return 'Inbound';
  if (key === 'outbound') return 'Outbound';
  return key;
}

function formatYAxisTick(value: number): string {
  if (value >= 1000) return `${Math.round(value / 1000)}k`;
  return String(value);
}

export default function WidgetCallHourlyChart({ config }: WidgetCallHourlyChartProps) {
  const { getToken } = useAuth();
  const [chartData, setChartData] = useState<ChartRow[]>([]);
  const [series, setSeries] = useState<string[]>(['inbound', 'outbound']);
  const [granularity, setGranularity] = useState<'hour' | 'day'>('hour');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const dateRange = (config?.date_range as WidgetDateRange | undefined) ?? 'today';
  const isDayView = granularity === 'day' || dateRange === 'week';

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const token = await getToken();
      if (!token || cancelled) return;
      const base = typeof window !== 'undefined' ? window.location.origin : '';
      const params = buildCallHistoryParams(config);
      setLoading(true);
      try {
        const res = await fetch(`${base}/api/calls/hourly?${params}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (cancelled) return;
        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          setError((data as { error?: string }).error || `HTTP ${res.status}`);
          setChartData([]);
          return;
        }
        const data = await res.json();
        setGranularity(data.granularity === 'day' ? 'day' : 'hour');
        const seriesKeys: string[] = Array.isArray(data.series)
          ? data.series
          : data.stack_by === 'wrap'
            ? []
            : ['inbound', 'outbound'];

        if (Array.isArray(data.buckets) && data.buckets.length > 0) {
          setSeries(seriesKeys);
          setChartData(
            data.buckets.map((b: ChartRow) => ({
              label: String(b.label ?? b.key ?? ''),
              ...seriesKeys.reduce(
                (acc, key) => {
                  acc[key] = Number(b[key]) || 0;
                  return acc;
                },
                {} as Record<string, number>
              ),
            }))
          );
        } else if (Array.isArray(data.hours) && data.hours.length > 0) {
          setSeries(['inbound', 'outbound']);
          setChartData(
            data.hours.map((h: { hour: number; inbound: number; outbound: number }) => ({
              label: String(h.hour),
              inbound: h.inbound ?? 0,
              outbound: h.outbound ?? 0,
            }))
          );
        } else {
          setChartData([]);
          setSeries(seriesKeys);
        }
        setError(null);
      } catch (e) {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : 'Failed to load');
          setChartData([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    getToken,
    config?.scope,
    config?.campaign_id,
    config?.ipscape_agent_id,
    config?.date_range,
    config?.chart_stack,
  ]);

  const hasData = useMemo(
    () => chartData.some((row) => series.some((key) => (Number(row[key]) || 0) > 0)),
    [chartData, series]
  );

  const chartMargin = { top: 8, right: 12, left: 8, bottom: isDayView ? 22 : 12 };

  return (
    <div
      style={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        padding: '0.25rem 0.5rem 0',
        minHeight: 140,
      }}
    >
      {loading && (
        <p style={{ color: 'var(--text-muted)', fontSize: '0.875rem', margin: 'auto 0', textAlign: 'center' }}>
          Loading…
        </p>
      )}
      {error && (
        <p style={{ color: 'var(--error)', fontSize: '0.875rem', margin: 'auto 0', textAlign: 'center' }}>
          {error}
        </p>
      )}
      {!loading && !error && !hasData && (
        <p style={{ color: 'var(--text-muted)', fontSize: '0.875rem', margin: 'auto 0', textAlign: 'center' }}>
          No calls in range
        </p>
      )}
      {!loading && !error && hasData && (
        <div style={{ flex: 1, minHeight: 100, width: '100%' }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData} margin={chartMargin}>
              <XAxis
                dataKey="label"
                tick={{ fill: 'var(--text-muted)', fontSize: isDayView ? 9 : 10 }}
                interval={isDayView ? 0 : 2}
                angle={isDayView ? -35 : 0}
                textAnchor={isDayView ? 'end' : 'middle'}
                height={isDayView ? 48 : 28}
                axisLine={{ stroke: 'var(--header-border)' }}
                tickLine={false}
              />
              <YAxis
                allowDecimals={false}
                width={44}
                tick={{ fill: 'var(--text-muted)', fontSize: 10 }}
                tickFormatter={formatYAxisTick}
                axisLine={false}
                tickLine={false}
              />
              <Tooltip
                contentStyle={{
                  background: 'var(--widget-bg)',
                  border: '1px solid var(--header-border)',
                  borderRadius: 6,
                  fontSize: '0.75rem',
                  maxWidth: 280,
                }}
                labelFormatter={(label) => (isDayView ? String(label) : `${label}:00`)}
                formatter={(value, name) => [Number(value) || 0, seriesDisplayName(String(name))]}
              />
              <Legend
                wrapperStyle={{ fontSize: '0.65rem', color: 'var(--text-muted)', paddingTop: 4 }}
                iconSize={8}
                formatter={(value) => seriesDisplayName(String(value))}
              />
              {series.map((key, i) => (
                <Bar
                  key={key}
                  dataKey={key}
                  stackId="calls"
                  fill={colorForStackSeries(key, i)}
                  name={key}
                  radius={i === series.length - 1 ? [2, 2, 0, 0] : [0, 0, 0, 0]}
                />
              ))}
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}
